import { useState } from 'react'
import { usePerson } from '../../contexts/PersonContext'
import { ParentNoteDialog } from '../messaging/ParentNoteDialog'
import { Avatar } from '../ui/Avatar'
import { InviteFamilyButton } from './InviteFamilyButton'

/** A family row as projected by the server (COTC_Circles R2); first names + ages only. */
export interface CircleFamilyRow {
  familyId: string
  family: string
  since: string
  kids: string
  games: string
  vouch: string
  initials: string
  tintIndex: number
  isSelf: boolean
}

interface CircleFamiliesTableProps {
  circleId: string
  families: CircleFamilyRow[]
}

/**
 * Friend/family circle body (COTC_Circles R2, DESIGN §3.3). Dashed empty state with an
 * "Invite a family" CTA when nobody has joined, else the families table.
 */
export function CircleFamiliesTable({ circleId, families }: CircleFamiliesTableProps) {
  const { role } = usePerson()
  const [target, setTarget] = useState<CircleFamilyRow | null>(null)

  if (families.length === 0) {
    return (
      <div className="rounded-card border border-dashed border-line bg-paper-row px-[30px] py-11 text-center">
        <div className="mb-2 font-serif text-[20px] font-medium text-ink">Nobody in here yet.</div>
        <p className="mx-auto mb-[18px] max-w-[44ch] text-[13.5px] leading-relaxed text-ink-muted">
          Invite a family you already trust, or move families over from another circle. They'll need
          to accept before your kids can see each other.
        </p>
        <div className="flex justify-center">
          {role === 'parent' && <InviteFamilyButton circleId={circleId} />}
        </div>
      </div>
    )
  }

  return (
    <div className="overflow-hidden rounded-card border border-line-soft">
      <div className="hidden grid-cols-[1.5fr_1fr_1.1fr_0.8fr_110px] gap-[14px] border-b border-line-soft bg-paper-panel px-5 py-[11px] font-mono text-[10.5px] uppercase tracking-label text-ink-faint xl:grid">
        <div>Family</div>
        <div>Kids</div>
        <div>Plays</div>
        <div>Vouched by</div>
        <div />
      </div>
      {families.map((f) => (
        <div
          key={f.familyId}
          className="grid min-w-0 grid-cols-1 items-center gap-3 border-b border-line-row px-5 py-[15px] last:border-b-0 xl:grid-cols-[1.5fr_1fr_1.1fr_0.8fr_110px] xl:gap-[14px]"
        >
          <div className="flex min-w-0 items-center gap-[11px]">
            <Avatar initials={f.initials || '•'} tintIndex={f.tintIndex ?? 0} size={32} />
            <div className="min-w-0">
              <div className="truncate text-[13.5px] font-medium text-ink">{f.family}</div>
              <div className="text-[11.5px] text-ink-faint">{f.since}</div>
            </div>
          </div>
          <div className="min-w-0 break-words text-[13px] leading-snug text-ink-muted"><span className="mr-2 text-ink-faint xl:hidden">Kids:</span>{f.kids}</div>
          <div className="min-w-0 break-words text-[12.5px] leading-snug text-ink-muted"><span className="mr-2 text-ink-faint xl:hidden">Plays:</span>{f.games}</div>
          <div className="min-w-0 break-words text-[12.5px] leading-snug text-ink-muted"><span className="mr-2 text-ink-faint xl:hidden">Vouched by:</span>{f.vouch}</div>
          <div className="text-right">
            {role === 'parent' && !f.isSelf && <button
              type="button"
              onClick={() => setTarget(f)}
              className="rounded-btn border border-line bg-transparent px-3 py-[7px] text-[12px] font-medium text-ink-muted hover:border-terra hover:text-terra-deep"
            >
              Message
            </button>}
          </div>
        </div>
      ))}
      {target && <ParentNoteDialog open toFamilyId={target.familyId} toFamilyName={target.family} onClose={() => setTarget(null)} />}
    </div>
  )
}
